import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Table, Badge, Button, Input, Label, Spinner } from 'reactstrap';
import { H5, H6 } from '../../../AbstractElements';
import { ArrowLeft, Download, Search, Truck, User, Calendar, TrendingUp, Activity, Users, MapPin, Printer, FileText } from 'react-feather';
import { useReactToPrint } from 'react-to-print';
import { apiPost } from '../../../Utils';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../Context/AuthContext';
import DataTable from 'react-data-table-component';

const VehicleDriverUsageReport = () => {
    const { hasPermission } = useAuth();
    const componentRef = useRef(null);

    // Dropdown Data
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [drivers, setDrivers] = useState<any[]>([]);

    // History Filters State
    const [historyBranchId, setHistoryBranchId] = useState('');
    const [historyVehicleId, setHistoryVehicleId] = useState('');
    const [historyDriverId, setHistoryDriverId] = useState('');
    const [historyVehicles, setHistoryVehicles] = useState<any[]>([]); // Filtered vehicles for history dropdown

    // History Data State
    const [driverHistory, setDriverHistory] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Sorting State
    const [sortBy, setSortBy] = useState('from');
    const [sortOrder, setSortOrder] = useState('desc');

    // History Pagination State
    const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(1);
    const [historyTotalRecords, setHistoryTotalRecords] = useState(0);
    const [historyPerPage, setHistoryPerPage] = useState(10);

    const printRef = useRef(null);
    const [printData, setPrintData] = useState<any[]>([]);

    const historyColumns = [
        {
            name: <div>Sl.No</div>,
            cell: (row: any, index: number) => (historyCurrentPage - 1) * historyPerPage + index + 1,
            width: '70px',
            center: true,
        },
        {
            name: <div>Branch</div>,
            selector: (row: any) => row.branch,
            sortable: true,
            wrap: true,
            sortField: 'branch',
            cell: (row: any) => (
                <span>
                    {row.branch || row.branch_name}
                    {row.branch_deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                </span>
            )
        },
        {
            name: <div>Vehicle</div>,
            selector: (row: any) => row.vehicle,
            sortable: true,
            cell: (row: any) => (
                <span className="">
                    <span className="text-primary">{row.vehicle || row.registration_number}</span>
                    {row.vehicle_deleted_at && <div className="text-danger small" style={{ fontSize: '0.75rem' }}>(Deleted)</div>}
                </span>
            ),
            wrap: true,
            sortField: 'vehicle'
        },
        {
            name: <div>Driver</div>,
            selector: (row: any) => row.driver,
            sortable: true,
            wrap: true,
            sortField: 'driver',
            cell: (row: any) => (
                <span>
                    {row.driver || row.driver_name}
                    {row.driver_deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                </span>
            )
        },
        {
            name: <div>Start Date</div>,
            selector: (row: any) => row.from,
            sortable: true,
            wrap: true,
            cell: (row: any) => row.from ? new Date(row.from).toLocaleString('en-GB') : '-',
            sortField: 'from'
        },
        {
            name: <div>End Date</div>,
            selector: (row: any) => row.to,
            sortable: true,
            wrap: true,
            cell: (row: any) => row.to === 'Present' ? <Badge color="success">Present</Badge> : (row.to ? new Date(row.to).toLocaleString('en-GB') : '-'),
            sortField: 'to'
        },
        {
            name: <div>Start Odometer</div>,
            selector: (row: any) => row.start_odometer,
            sortable: true,
            wrap: true,
            cell: (row: any) => row.start_odometer ? Number(row.start_odometer).toLocaleString() : '-',
            sortField: 'start_odometer'
        },
        {
            name: <div>End Odometer</div>,
            selector: (row: any) => row.end_odometer,
            sortable: true,
            wrap: true,
            cell: (row: any) => row.end_odometer ? Number(row.end_odometer).toLocaleString() : '-',
            sortField: 'end_odometer'
        },
        {
            name: <div>Kilometer</div>,
            sortable: true,
            sortField: 'kilometer',
            cell: (row: any) => {
                if (row.start_odometer && row.end_odometer) {
                    return (Number(row.end_odometer) - Number(row.start_odometer)).toLocaleString();
                }
                return '-';
            },
        },
        {
            name: <div>Duration</div>,
            selector: (row: any) => row.duration,
            sortable: true,
            sortField: 'duration',
            cell: (row: any) => (
                <Badge color="light" className="text-dark border">
                    {row.duration}
                </Badge>
            )
        }
    ];

    // Fetch Dropdown Data
    useEffect(() => {
        apiPost("branches/list").then((res: any) => {
            setBranches(Array.isArray(res) ? res : []);
        }).catch(err => console.error(err));

        apiPost("drivers/list").then((res: any) => {
            setDrivers(Array.isArray(res) ? res : []);
        }).catch(err => console.error(err));
    }, []);

    // Fetch vehicles for history filter when branch changes
    useEffect(() => {
        const payload: any = {};
        if (historyBranchId) payload.branch_id = historyBranchId;
        // Reset vehicle selection
        setHistoryVehicleId('');

        apiPost("vehicles/list", payload).then((res: any) => {
            setHistoryVehicles(Array.isArray(res) ? res : []);
        }).catch(() => setHistoryVehicles([]));
    }, [historyBranchId]);

    // Fetch Driver History (Main Function)
    const fetchDriverHistory = () => {
        setHistoryLoading(true);
        const payload: any = {
            page: historyCurrentPage,
            per_page: historyPerPage,
            sort_by: sortBy,
            sort_order: sortOrder
        };
        if (historyBranchId) payload.branch_id = historyBranchId;
        if (historyVehicleId) payload.vehicle_id = historyVehicleId;
        if (historyDriverId) payload.driver_id = historyDriverId;

        apiPost("reports/driver-usage-history", payload).then((res: any) => {
            if (res) {
                setDriverHistory(res.data || []);
                setHistoryTotalRecords(res.total || 0);
                setHistoryTotalPages(res.last_page || 1);
            }
            setHistoryLoading(false);
        }).catch(err => {
            console.error(err);
            setHistoryLoading(false);
        });
    };

    // Initial Fetch & Update on Filter/Page/Sort Change
    useEffect(() => {
        fetchDriverHistory();
    }, [historyBranchId, historyVehicleId, historyDriverId, historyCurrentPage, historyPerPage, sortBy, sortOrder]);

    // Reset page when filters change
    useEffect(() => {
        setHistoryCurrentPage(1);
        setSortBy('from');
        setSortOrder('desc');
    }, [historyBranchId, historyVehicleId, historyDriverId]);

    const handleHistoryPageChange = (newPage: number) => {
        setHistoryCurrentPage(newPage);
    };

    const handleHistoryPerRowsChange = (newPerPage: number, page: number) => {
        setHistoryPerPage(newPerPage);
        setHistoryCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        // Ensure we are getting a valid string identifier for sorting
        const field = column.sortField || 'date';
        setSortBy(field);
        setSortOrder(sortDirection);
    };

    const handleDownload = async () => {
        setHistoryLoading(true);
        try {
            const payload: any = {
                page: 1,
                per_page: historyTotalRecords > 0 ? historyTotalRecords : 10000,
                sort_by: sortBy,
                sort_order: sortOrder
            };
            if (historyBranchId) payload.branch_id = historyBranchId;
            if (historyVehicleId) payload.vehicle_id = historyVehicleId;
            if (historyDriverId) payload.driver_id = historyDriverId;

            const res: any = await apiPost("reports/driver-usage-history", payload);
            const allData = res.data || [];

            const doc = new jsPDF() as any;
            doc.text("Driver Usage History Report", 14, 20);
            doc.setFontSize(10);
            doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 26);

            const tableRows = allData.map((item: any, index: number) => {
                const startOdo = item.start_odometer ? Number(item.start_odometer) : 0;
                const endOdo = item.end_odometer ? Number(item.end_odometer) : 0;
                const km = (startOdo && endOdo) ? (endOdo - startOdo).toLocaleString() : '-';

                return [
                    index + 1,
                    (item.branch || item.branch_name || '-') + (item.branch_deleted_at ? ' (Deleted)' : ''),
                    (item.vehicle || item.registration_number || '-') + (item.vehicle_deleted_at ? ' (Deleted)' : ''),
                    (item.driver || item.driver_name || '-') + (item.driver_deleted_at ? ' (Deleted)' : ''),
                    item.from ? new Date(item.from).toLocaleString('en-GB') : '-',
                    item.to ? (item.to === 'Present' ? 'Present' : new Date(item.to).toLocaleString('en-GB')) : '-',
                    item.start_odometer ? Number(item.start_odometer).toLocaleString() : '-',
                    item.end_odometer ? Number(item.end_odometer).toLocaleString() : '-',
                    km,
                    item.duration || '-'
                ];
            });

            autoTable(doc, {
                startY: 35,
                head: [['Sl.No', 'Branch', 'Vehicle', 'Driver', 'Start Date', 'End Date', 'Start Odo', 'End Odo', 'KM', 'Duration']],
                body: tableRows,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [0, 150, 136] }
            });

            doc.save('Driver_Usage_History.pdf');
        } catch (error) {
            console.error("Error downloading report", error);
        } finally {
            setHistoryLoading(false);
        }
    };

    // Print Handler with Data Fetching
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onBeforeGetContent: async () => {
            setHistoryLoading(true);
            try {
                const payload: any = {
                    page: 1,
                    per_page: historyTotalRecords > 0 ? historyTotalRecords : 10000,
                    sort_by: sortBy,
                    sort_order: sortOrder
                };
                if (historyBranchId) payload.branch_id = historyBranchId;
                if (historyVehicleId) payload.vehicle_id = historyVehicleId;
                if (historyDriverId) payload.driver_id = historyDriverId;

                const res: any = await apiPost("reports/driver-usage-history", payload);
                setPrintData(res.data || []);
                // Allow a small delay for state update to propagate to the print DOM
                return new Promise((resolve) => {
                    setTimeout(resolve, 500);
                });
            } catch (error) {
                console.error("Error fetching print data", error);
            } finally {
                setHistoryLoading(false);
            }
        },
    });

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '14px',
                fontWeight: '600',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };
    return (
        <div className="page-body">
            <Breadcrumbs parent="Reports" title="Driver Usage History" pageTitle="Driver Usage History" />
            <div className="container-fluid">
                <Row className="d-print-none mb-3">
                    <Col md={4}>
                        <Card className="h-80 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #ffffff 100%)', borderLeft: '4px solid #009688' }}>
                            <CardBody className="py-3 d-flex align-items-center justify-content-between">
                                <div>
                                    <h6 className="text-muted text-uppercase small fw-bold mb-2">Total Records</h6>
                                    <h4 className="text-dark mb-0 fw-bold">{historyTotalRecords}</h4>
                                </div>
                                <div className="p-3 bg-white rounded-circle shadow-sm">
                                    <FileText size={24} style={{ color: '#009688' }} />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4}>

                    </Col>

                    <Col md={4} className="bg-transparent border-0 d-flex justify-content-end align-items-center px-0 flex-wrap gap-2">
                        {hasPermission('vehicle-driver-usage-report.download') && (
                            <Button
                                color="success"
                                className="px-4 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm"
                                onClick={handleDownload}
                            >
                                <Download size={16} /> <span className="fw-bold">Download</span>
                            </Button>
                        )}
                        {hasPermission('vehicle-driver-usage-report.print') && (
                            <Button
                                color="primary"
                                className="px-4 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm"
                                onClick={handlePrint}
                            >
                                <Printer size={16} /> <span className="fw-bold">Print</span>
                            </Button>
                        )}
                    </Col>
                </Row>


                {/* Filters */}
                <Card className="border-0 shadow-sm mb-4 bg-light">
                    <CardBody className="p-4">
                        <Row className="g-3">
                            <Col md={4}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <MapPin size={14} style={{ color: '#009688' }} /> Select Branch
                                </Label>
                                <Input
                                    type="select"
                                    value={historyBranchId}
                                    onChange={(e) => setHistoryBranchId(e.target.value)}
                                    bsSize="sm"
                                    className="form-control"
                                    style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem' }}
                                >
                                    <option value="">All Branches</option>
                                    {branches.map((b: any) => (
                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                    ))}
                                </Input>
                            </Col>
                            <Col md={4}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Truck size={14} style={{ color: '#009688' }} /> Filter Vehicle
                                </Label>
                                <Input
                                    type="select"
                                    value={historyVehicleId}
                                    onChange={(e) => setHistoryVehicleId(e.target.value)}
                                    bsSize="sm"
                                    className="form-control"
                                    style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem' }}
                                >
                                    <option value="">All Vehicles</option>
                                    {historyVehicles.map((v: any) => (
                                        <option key={v.id} value={v.id}>{v.registration_number}</option>
                                    ))}
                                </Input>
                            </Col>
                            <Col md={4}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <User size={14} style={{ color: '#009688' }} /> Filter Driver
                                </Label>
                                <Input
                                    type="select"
                                    value={historyDriverId}
                                    onChange={(e) => setHistoryDriverId(e.target.value)}
                                    bsSize="sm"
                                    className="form-control"
                                    style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem' }}
                                >
                                    <option value="">All Drivers</option>
                                    {drivers.map((d: any) => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </Input>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Table */}
                {historyLoading ? (
                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}>
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <div>
                        <div className="d-none d-print-block text-center mb-4">
                            <h4>Driver Usage History Report</h4>
                            <p className="text-muted">Generated: {new Date().toLocaleDateString()}</p>
                        </div>
                        <Card className="border-0 shadow-sm">
                            <CardBody className="p-0">
                                <div className="rounded-3 overflow-hidden">
                                    <DataTable
                                        columns={historyColumns}
                                        data={driverHistory}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={historyTotalRecords}
                                        paginationDefaultPage={historyCurrentPage}
                                        onChangePage={handleHistoryPageChange}
                                        onChangeRowsPerPage={handleHistoryPerRowsChange}
                                        sortServer
                                        onSort={handleSort}
                                        highlightOnHover
                                        responsive
                                        customStyles={customStyles}
                                        noDataComponent={
                                            <div className="py-5 text-center text-muted">
                                                <User size={48} className="mb-3 opacity-25" />
                                                <p className="mb-0">No driver usage history found matching the criteria</p>
                                            </div>
                                        }
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                )}

                {/* Print Component (Hidden) */}
                <div style={{ display: 'none' }}>
                    <div ref={printRef} style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
                        {/* Header matching PDF */}
                        <div style={{ marginBottom: '20px' }}>
                            <h2 style={{ fontSize: '18px', margin: '0 0 5px 0', fontWeight: 'bold' }}>Driver Usage History Report</h2>
                            <p style={{ fontSize: '10px', color: '#666', margin: 0 }}>Generated: {new Date().toLocaleDateString()}</p>
                        </div>



                        {/* Continuous Table matching PDF Columns */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', tableLayout: 'auto' }}>
                            <thead style={{ display: 'table-header-group' }}>
                                <tr style={{ backgroundColor: '#009688', color: '#fff' }}>
                                    <th style={{ padding: '6px', textAlign: 'center', fontWeight: 'bold', border: '1px solid #ddd' }}>Sl.No</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Branch</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Vehicle</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Driver</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Start Date</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>End Date</th>
                                    <th style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold', border: '1px solid #ddd' }}>Start Odo</th>
                                    <th style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold', border: '1px solid #ddd' }}>End Odo</th>
                                    <th style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold', border: '1px solid #ddd' }}>KM</th>
                                    <th style={{ padding: '6px', textAlign: 'center', fontWeight: 'bold', border: '1px solid #ddd' }}>Duration</th>
                                </tr>
                            </thead>
                            <tbody>
                                {printData.map((item, index) => {
                                    const startOdo = item.start_odometer ? Number(item.start_odometer) : 0;
                                    const endOdo = item.end_odometer ? Number(item.end_odometer) : 0;
                                    const km = (startOdo && endOdo) ? (endOdo - startOdo).toLocaleString() : '-';

                                    return (
                                        <tr key={index} style={{ pageBreakInside: 'avoid' }}>
                                            <td style={{ padding: '6px', textAlign: 'center', border: '1px solid #ddd' }}>{index + 1}</td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                                                {item.branch || item.branch_name || '-'}
                                                {item.branch_deleted_at && <span style={{ color: 'red', fontSize: '8px', marginLeft: '4px' }}>(Deleted)</span>}
                                            </td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                                                {item.vehicle || item.registration_number || '-'}
                                                {item.vehicle_deleted_at && <span style={{ color: 'red', fontSize: '8px', marginLeft: '4px' }}>(Deleted)</span>}
                                            </td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                                                {item.driver || item.driver_name || '-'}
                                                {item.driver_deleted_at && <span style={{ color: 'red', fontSize: '8px', marginLeft: '4px' }}>(Deleted)</span>}
                                            </td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>{item.from ? new Date(item.from).toLocaleString('en-GB') : '-'}</td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                                                {item.to === 'Present' ? <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>Present</span> : (item.to ? new Date(item.to).toLocaleString('en-GB') : '-')}
                                            </td>
                                            <td style={{ padding: '6px', textAlign: 'right', border: '1px solid #ddd', fontFamily: 'monospace' }}>
                                                {startOdo ? startOdo.toLocaleString() : '-'}
                                            </td>
                                            <td style={{ padding: '6px', textAlign: 'right', border: '1px solid #ddd', fontFamily: 'monospace' }}>
                                                {endOdo ? endOdo.toLocaleString() : '-'}
                                            </td>
                                            <td style={{ padding: '6px', textAlign: 'right', border: '1px solid #ddd', fontFamily: 'monospace' }}>
                                                {km}
                                            </td>
                                            <td style={{ padding: '6px', textAlign: 'center', border: '1px solid #ddd' }}>{item.duration || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Footer */}
                        <div style={{
                            marginTop: '20px',
                            paddingTop: '10px',
                            borderTop: '1px solid #eee',
                            textAlign: 'center',
                            color: '#999',
                            fontSize: '10px'
                        }}>
                            Fleet Management System © {new Date().getFullYear()}
                        </div>
                    </div>
                </div>
            </div>
            <style>
                {`
                    @media print {
                        .d-print-none { display: none !important; }
                        .d-print-block { display: block !important; }
                        .page-body { padding: 0; margin: 0; }
                        .card { border: none !important; box-shadow: none !important; }
                    }
                `}
            </style>
        </div >
    );
};

export default VehicleDriverUsageReport;
